import { createContext, useContext, useEffect, useId, useRef, useState, type ReactNode } from "react";
import fdLogo from "../assets/brand/FD-Logo-Blue.svg";
import billingIcon from "../assets/brand/Billing-Icon.svg";
import { Icon } from "./Icon";
import { CommandPalette } from "./CommandPalette";
import { SelectionIndicator } from "./SelectionIndicator";

const NavigationSelection = createContext<string | null>(null);

export interface ShellProps {
  sidebar: ReactNode;
  topbar?: ReactNode;
  mobileMark?: ReactNode;
  children: ReactNode;
}

export function Shell({ sidebar, topbar, children, mobileMark }: ShellProps) {
  // Below the sidebar breakpoint the sidebar collapses into an off-canvas
  // drawer opened by the topbar hamburger. Above it, `navOpen` is inert — the
  // sidebar is a static grid column and the trigger/scrim are display:none.
  const [navOpen, setNavOpen] = useState(false);
  const sidebarId = useId();
  const menuTrigger = useRef<HTMLButtonElement>(null);
  const sidebarRef = useRef<HTMLElement>(null);
  const navigationIndicator = useId();

  useEffect(() => {
    if (typeof window.matchMedia !== 'function') return;
    const desktop = window.matchMedia('(min-width: 1025px)');
    const onChange = () => { if (desktop.matches) setNavOpen(false); };
    desktop.addEventListener('change', onChange);
    return () => desktop.removeEventListener('change', onChange);
  }, []);

  // While the drawer is open: lock background scroll and close on Escape.
  useEffect(() => {
    if (!navOpen) return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const previousFocus = menuTrigger.current ?? document.activeElement as HTMLElement | null;
    sidebarRef.current?.querySelector<HTMLButtonElement>('button')?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") { e.preventDefault(); setNavOpen(false); }
      if (e.key === 'Tab') {
        const controls = Array.from(sidebarRef.current?.querySelectorAll<HTMLElement>('button:not(:disabled),a[href],select,input,summary') ?? []).filter(el => el.getClientRects().length > 0);
        const first = controls[0], last = controls[controls.length - 1];
        if (!first || !last) return;
        if (e.shiftKey && (document.activeElement === first || !sidebarRef.current?.contains(document.activeElement))) { e.preventDefault(); last.focus(); }
        else if (!e.shiftKey && (document.activeElement === last || !sidebarRef.current?.contains(document.activeElement))) { e.preventDefault(); first.focus(); }
      }
    };
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prevOverflow;
      window.removeEventListener("keydown", onKey);
      if (previousFocus?.isConnected) previousFocus.focus({ preventScroll: true });
    };
  }, [navOpen]);

  return (
    <NavigationSelection.Provider value={navigationIndicator}><div className={`fd-shell${navOpen ? " fd-shell--nav-open" : ""}`}>
      <aside
        id={sidebarId}
        ref={sidebarRef}
        role={navOpen ? "dialog" : undefined}
        aria-modal={navOpen || undefined}
        aria-label={navOpen ? "Navigation" : undefined}
        className="fd-sidebar"
        // Activating any nav control inside the drawer navigates; close it so
        // the destination is visible. Router-agnostic via event delegation.
        onClick={(e) => {
          if ((e.target as HTMLElement).closest("a, button")) setNavOpen(false);
        }}
      >
        <button
          type="button"
          className="fd-sidebar__close"
          aria-label="Close menu"
          onClick={() => setNavOpen(false)}
        >
          <Icon name="close" size={20} />
        </button>
        {sidebar}
      </aside>
      <button
        type="button"
        className="fd-sidebar__scrim"
        aria-hidden="true"
        tabIndex={-1}
        onClick={() => setNavOpen(false)}
      />
      <div className="fd-shell__body" inert={navOpen || undefined}>
        {topbar && (
          <header className="fd-topbar">
            <button
              type="button"
              ref={menuTrigger}
              className="fd-topbar__menu"
              aria-label="Open menu"
              aria-expanded={navOpen}
              aria-controls={sidebarId}
              onClick={() => setNavOpen(true)}
            >
              <Icon name="menu" size={20} />
            </button>
            <span className="fd-topbar__mark" aria-hidden="true">
              {mobileMark ?? <img src={billingIcon} alt="" />}
            </span>
            {topbar}
            <CommandPalette sidebarId={sidebarId} />
          </header>
        )}
        <main className="fd-main">{children}</main>
      </div>
    </div></NavigationSelection.Provider>
  );
}

/** Sticky topbar content helper: left context (title/breadcrumb), right actions. */
export function Topbar({ left, right }: { left?: ReactNode; right?: ReactNode }) {
  return (
    <>
      <div className="fd-topbar__left">{left}</div>
      <div className="fd-topbar__right">{right}</div>
    </>
  );
}

export interface SidebarBrandProps {
  logoSrc?: string;
  name?: string;
  sub?: string;
}

export function SidebarBrand({ logoSrc, name = "Future Demand", sub }: SidebarBrandProps) {
  return (
    <div className="fd-sidebar__brand">
      <span className="fd-sidebar__logo">
        <img src={logoSrc ?? fdLogo} alt={name} />
      </span>
      {sub && <div className="fd-sidebar__sub">{sub}</div>}
    </div>
  );
}

export function SidebarGroupLabel({ children }: { children: ReactNode }) {
  return <div className="fd-sidebar__group-label">{children}</div>;
}

export function SidebarSpacer() {
  return <div className="fd-sidebar__spacer" />;
}

export function SidebarFooter({ children }: { children: ReactNode }) {
  return <div className="fd-sidebar__footer">{children}</div>;
}

export interface NavItemProps {
  icon?: ReactNode;
  active?: boolean;
  onClick?: () => void;
  href?: string;
  /** Independent selections, e.g. organization picker versus page navigation. */
  selectionGroup?: string;
  children: ReactNode;
}

export function NavItem({ icon, active, onClick, href, children, selectionGroup = "navigation" }: NavItemProps) {
  const indicator = useContext(NavigationSelection);
  const cls = ["fd-navitem", active && "fd-navitem--active"].filter(Boolean).join(" ");
  const inner = (
    <>
      {active && indicator && <SelectionIndicator id={`${indicator}-${selectionGroup}`} navigation />}
      {icon && <span className="fd-navitem__icon">{icon}</span>}
      <span className="fd-navitem__label">{children}</span>
    </>
  );
  if (href) {
    return (
      <a className={cls} href={href} aria-current={active ? "page" : undefined}>
        {inner}
      </a>
    );
  }
  return (
    <button type="button" className={cls} onClick={onClick} aria-current={active ? "page" : undefined}>
      {inner}
    </button>
  );
}

/** Keeps infrequent destinations available without competing with daily work. */
export function SidebarDisclosure({ label, icon, active = false, children }: { label: string; icon?: ReactNode; active?: boolean; children: ReactNode }) {
  const [open, setOpen] = useState(active);
  useEffect(() => { if (active) setOpen(true); }, [active]);
  return <details className="fd-sidebar-disclosure" open={open} onToggle={event => setOpen(event.currentTarget.open)}>
    <summary data-active={active || undefined}>{icon}<span>{label}</span><Icon name="chevronDown" size={15} /></summary>
    <div className="fd-sidebar-disclosure__items">{children}</div>
  </details>;
}
